import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ROOMS = [
  { id: "single", label: "Single Occupancy", price: 3394, capacity: 1, note: "One guest in a private room." },
  { id: "double", label: "Standard Double Room", price: 3044, capacity: 2, note: "Two guests sharing a room." },
  { id: "triple", label: "Triple Sharing", price: 2799, capacity: 3, note: "Three guests sharing one room.", limited: true },
];

const usd = (n) => `$${n.toLocaleString("en-US")} USD`;
const inputCls =
  "w-full bg-[#F4F1EA] border border-[#D4CFC4] px-4 py-3 text-base font-light text-[#2C3029] placeholder:text-[#8A8D82] focus:outline-none focus:border-[#9E6D55] transition-colors";
const labelCls = "block text-[10px] uppercase tracking-[0.3em] text-[#9E6D55] mb-2";

const RoomOption = ({ room, selected, onSelect }) => (
  <button
    type="button"
    data-testid={`inquiry-room-${room.id}`}
    aria-pressed={selected}
    onClick={() => onSelect(room.id)}
    className={`text-left p-5 border transition-colors duration-300 ${
      selected ? "border-[#9E6D55] bg-[#E8E4D9]" : "border-[#D4CFC4] bg-[#F4F1EA] hover:border-[#9E6D55]/60"
    }`}
  >
    <span className="block text-[10px] uppercase tracking-[0.3em] text-[#9E6D55]">{room.label}</span>
    <span className="block mt-2 font-heading text-2xl text-[#2C3029]">{usd(room.price)}</span>
    <span className="block text-xs italic text-[#5C6055]">per person &middot; full seven-night retreat</span>
    <span className="block mt-2 text-sm font-light text-[#3F433A]">{room.note}</span>
    {room.limited && (
      <span className="inline-block mt-3 text-[9px] uppercase tracking-[0.25em] px-2.5 py-1 bg-[#2C3029] text-[#F4F1EA]">
        Limited Availability &mdash; Only 1 Room
      </span>
    )}
  </button>
);

const SharingChoice = ({ room, guests, form, set }) => {
  if (room.capacity === 1 || guests >= room.capacity) return null;
  const open = room.capacity - guests;
  return (
    <div data-testid="inquiry-sharing" className="space-y-3">
      <span className={labelCls}>Who fills the remaining {open === 1 ? "bed" : `${open} beds`}?</span>
      {[
        ["together", "Someone I already know (they will send their own inquiry)"],
        ["pair_me", "Please pair me with another retreat participant (subject to availability)"],
      ].map(([val, text]) => (
        <label key={val} className="flex items-start gap-3 cursor-pointer text-base font-light text-[#2C3029]">
          <input
            type="radio"
            name="sharing"
            value={val}
            data-testid={`inquiry-sharing-${val}`}
            checked={form.sharing === val}
            onChange={() => set("sharing", val)}
            className="mt-1.5 accent-[#9E6D55]"
          />
          {text}
        </label>
      ))}
      {form.sharing === "together" && (
        <input
          data-testid="inquiry-roommate-names"
          className={inputCls}
          placeholder="Their name(s)"
          value={form.roommate_names}
          onChange={(e) => set("roommate_names", e.target.value)}
        />
      )}
    </div>
  );
};

const INITIAL = { full_name: "", email: "", phone: "", room_type: "double", guests: 1, sharing: "", roommate_names: "", message: "" };

const InquiryForm = () => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState({ state: "idle" });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const room = ROOMS.find((r) => r.id === form.room_type);
  const needsSharing = room.capacity > 1 && form.guests < room.capacity;

  const pickRoom = (id) => {
    const r = ROOMS.find((x) => x.id === id);
    setForm((f) => ({ ...f, room_type: id, guests: Math.min(f.guests, r.capacity), sharing: "" }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (needsSharing && !form.sharing) {
      setStatus({ state: "error", msg: "Please choose how you would like to share the room." });
      return;
    }
    setStatus({ state: "loading" });
    try {
      const payload = { ...form, sharing: needsSharing ? form.sharing : null, phone: form.phone || null };
      const { data } = await axios.post(`${API}/inquiries`, payload);
      setStatus({ state: "done", data });
      setForm(INITIAL);
    } catch (err) {
      const d = err.response?.data?.detail;
      setStatus({ state: "error", msg: typeof d === "string" ? d : "Please check your details and try again." });
    }
  };

  if (status.state === "done") return <InquiryReceived data={status.data} onReset={() => setStatus({ state: "idle" })} />;

  return (
    <form data-testid="booking-inquiry-form" onSubmit={submit} className="space-y-8">
      <div>
        <span className={labelCls}>Room preference</span>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {ROOMS.map((r) => (
            <RoomOption key={r.id} room={r} selected={r.id === form.room_type} onSelect={pickRoom} />
          ))}
        </div>
        {room.limited && (
          <p data-testid="inquiry-triple-notice" className="mt-4 text-sm italic text-[#9E6D55]">
            Only one triple room exists. This option is subject to manual confirmation and may no longer be available.
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-end">
        <div>
          <label className={labelCls} htmlFor="inquiry-guests">Number of guests in this inquiry</label>
          <select
            id="inquiry-guests"
            data-testid="inquiry-guests-select"
            className={inputCls}
            value={form.guests}
            disabled={room.capacity === 1}
            onChange={(e) => setForm((f) => ({ ...f, guests: Number(e.target.value), sharing: "" }))}
          >
            {Array.from({ length: room.capacity }, (_, i) => i + 1).map((n) => (
              <option key={n} value={n}>{n} {n === 1 ? "guest" : "guests"}</option>
            ))}
          </select>
        </div>
        <div data-testid="inquiry-total" className="border-l-2 border-[#9E6D55] pl-5">
          <span className="block text-[10px] uppercase tracking-[0.3em] text-[#9E6D55]">Estimated total</span>
          <span data-testid="inquiry-total-amount" className="block font-heading text-3xl text-[#2C3029]">
            {usd(room.price * form.guests)}
          </span>
          <span className="block text-xs italic text-[#5C6055]">
            {form.guests} &times; {usd(room.price)} per person &middot; full seven-night retreat
          </span>
        </div>
      </div>

      <SharingChoice room={room} guests={form.guests} form={form} set={set} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <input data-testid="inquiry-name" required minLength={2} className={inputCls} placeholder="Full name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} />
        <input data-testid="inquiry-email" required type="email" className={inputCls} placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} />
        <input data-testid="inquiry-phone" className={inputCls} placeholder="Phone / WhatsApp (optional)" value={form.phone} onChange={(e) => set("phone", e.target.value)} />
      </div>
      <textarea data-testid="inquiry-message" rows={3} className={inputCls} placeholder="Anything we should know? (optional)" value={form.message} onChange={(e) => set("message", e.target.value)} />

      {status.state === "error" && (
        <p data-testid="inquiry-error" className="text-sm text-[#9E3D2C]">{status.msg}</p>
      )}
      <div className="flex flex-col md:flex-row md:items-center gap-5">
        <button
          type="submit"
          data-testid="inquiry-submit-button"
          disabled={status.state === "loading"}
          className="inline-block bg-[#2C3029] text-[#F4F1EA] px-10 py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#9E6D55] transition-colors duration-500 disabled:opacity-60"
        >
          {status.state === "loading" ? "Sending…" : "Send Booking Inquiry"}
        </button>
        <p className="text-xs italic text-[#5C6055] max-w-md">
          This is an inquiry, not a confirmed booking. No payment is taken here. Our team confirms room availability manually and will reply by email.
        </p>
      </div>
    </form>
  );
};

const InquiryReceived = ({ data, onReset }) => (
  <div data-testid="inquiry-success" className="border border-[#9E6D55]/40 bg-[#F4F1EA] p-8 space-y-4">
    <p className="font-heading italic text-2xl text-[#2C3029]">Thank you &mdash; your inquiry has been received.</p>
    <p className="text-base font-light text-[#3F433A]">
      {data.room_label} &middot; {data.guests} {data.guests === 1 ? "guest" : "guests"} &middot; estimated {usd(data.estimated_total)}
    </p>
    <p data-testid="inquiry-success-not-confirmed" className="text-base font-light text-[#3F433A]">
      <span className="text-[#2C3029]">Your room is not confirmed yet.</span> Our team will check availability and reply by email. Your place is only secured once we confirm your room and your deposit is received.
    </p>
    <button type="button" data-testid="inquiry-send-another" onClick={onReset} className="text-[11px] uppercase tracking-[0.25em] text-[#9E6D55] underline underline-offset-4">
      Send another inquiry
    </button>
  </div>
);

export const BookingInquiry = () => {
  const [target, setTarget] = useState(null);
  useEffect(() => setTarget(document.getElementById("booking-inquiry-root")), []);
  if (!target) return null;
  return createPortal(
    <section data-testid="booking-inquiry-section" className="mt-24 max-w-5xl mx-auto">
      <div className="flex items-center gap-4 mb-4 justify-center">
        <span className="block w-10 h-px bg-[#9E6D55]" />
        <h3 className="font-heading text-2xl md:text-3xl font-light tracking-tight text-[#2C3029]">Booking Inquiry</h3>
        <span className="block w-10 h-px bg-[#9E6D55]" />
      </div>
      <p className="text-center text-base font-light text-[#5C6055] mb-10 max-w-2xl mx-auto">
        October 12&ndash;19, 2027 &middot; 7 nights &middot; 8 days. Choose your room and tell us who you&rsquo;re travelling with. Prices are per person for the full seven-night retreat.
      </p>
      <InquiryForm />
    </section>,
    target
  );
};

export default BookingInquiry;
